/**
 * THE ONE ROUTE TREE (PRD-625): the main app's screens and the review cockpit's, in one tree and one
 * `Register`, so every typed `to` is checked against all of them. Both entries (`index.html`, `admin.html`)
 * compile together and a module augmentation is one declaration per compilation, so a second router for the
 * admin shell would leave its links checked against the main app's tree (TS2322, measured 2026-10-15). Each
 * entry builds its router from this tree and REFUSES the other's paths: the main entry sends `/cockpit/...`
 * to `/projects`, the admin entry sends every main path to the cockpit's picker. The cockpit's screens load
 * lazily (`cockpit.lazy.tsx`), so the main bundle does not carry them. Modules are composed here and
 * nowhere else (docs/FRONTEND.md law 3).
 */
import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router';
import { parseFilterSearch } from '../api';
import { ADDIN_CALLBACK_PATH, AddinCallback, type User } from '../modules/auth';
import { ReviewPanel } from '../modules/review';
import { TakeoffPanel } from '../modules/takeoff';
import { Frame, RouteLink, Stack, Title } from '../strata';
import {
  parseArticleSearch,
  parseHeldSearch,
  parseKindsSearch,
  parseRuleSearch,
  parseWalkSearch,
} from './cockpit-search';
import { Overview } from './Overview';
import { ProjectTabs } from './ProjectTabs';
import { Workspace } from './Workspace';

/** Which page a router serves: the main app, or admin's screens. */
export type Entry = 'main' | 'admin';

/** What every route may read of the entry it is running under. */
export interface RouterContext {
  readonly entry: Entry;
}

export const COCKPIT_PREFIX = '/cockpit';

function inCockpit(pathname: string): boolean {
  return pathname === COCKPIT_PREFIX || pathname.startsWith(`${COCKPIT_PREFIX}/`);
}

// Each entry refuses the other's paths, before any route loads.
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  beforeLoad: ({ context, location }) => {
    switch (context.entry) {
      case 'main':
        if (inCockpit(location.pathname)) throw redirect({ to: '/projects' });
        return;
      case 'admin':
        if (!inCockpit(location.pathname)) throw redirect({ to: '/cockpit' });
        return;
    }
  },
});

// Routes are the frontend's public surface; future modules contribute
// theirs and this tree composes them. The project id rides the PATH
// (APP_SPEC): every screen under a project is a link you can send.
let sessionUser: User | null = null;

/** The signed-in person the main screens render for; the main app's gate sets it before the router mounts. */
export function setSessionUser(user: User | null): void {
  sessionUser = user;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/projects' });
  },
});

// The journey's first screen (APP_SPEC box 3): sign in, see your
// projects, and every card is a link into its workspace.
const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects',
  component: Overview,
});

// The filter rides the search params (APP_SPEC "Filtering is
// first-class"): the RuleSelector vocabulary, typed here once;
// modules read it via useSearch without importing the shell.
const projectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId',
  validateSearch: parseFilterSearch,
  component: ProjectScreen,
});

function ProjectScreen() {
  const { projectId } = projectRoute.useParams();
  return sessionUser ? <Workspace user={sessionUser} projectId={projectId} /> : null;
}

// Sibling product screens under the project param (APP_SPEC): each a
// module surface the shell frames; the filter search params carry
// across tabs unchanged.
const takeoffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId/takeoff',
  validateSearch: parseFilterSearch,
  component: TakeoffScreen,
});

function TakeoffScreen() {
  const { projectId } = takeoffRoute.useParams();
  if (!sessionUser) return null;
  return (
    <Frame>
      <RouteLink to="/projects">← All projects</RouteLink>
      <Stack direction="row" gap="lg" align="center" wrap>
        <Title level={2}>{projectId}</Title>
        <ProjectTabs projectId={projectId} active="takeoff" />
      </Stack>
      <TakeoffPanel projectId={projectId} />
    </Frame>
  );
}

// The reviewer's screen (APP_SPEC box 6, PRD-159): the pending
// attestations and the findings, each with its citation, under the
// same project param; the filter search rides across unchanged.
const reviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId/review',
  validateSearch: parseFilterSearch,
  component: ReviewScreen,
});

function ReviewScreen() {
  const { projectId } = reviewRoute.useParams();
  if (!sessionUser) return null;
  return (
    <Frame>
      <RouteLink to="/projects">← All projects</RouteLink>
      <Stack direction="row" gap="lg" align="center" wrap>
        <Title level={2}>{projectId}</Title>
        <ProjectTabs projectId={projectId} active="review" />
      </Stack>
      <ReviewPanel projectId={projectId} />
    </Frame>
  );
}

// The front door's second step (sign in -> onboard -> upload): a
// signed-in user with no organization lands here from the projects
// screen or the upload panel's refusal message.
const onboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboard',
  component: OnboardScreen,
});

function OnboardScreen() {
  if (!sessionUser) return null;
  return (
    <Frame>
      <RouteLink to="/projects">← All projects</RouteLink>
    </Frame>
  );
}

// The Revit add-in's sign-in return (PRD-553): past the auth gate, so
// the person is signed in; the screen hands the ID token to the
// add-in's loopback listener.
const addinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: ADDIN_CALLBACK_PATH,
  component: () => (sessionUser ? <AddinCallback user={sessionUser} /> : null),
});


// The cockpit (PRD-625): a frame with the six screens under it, each with deep-linkable search. The
// components are lazy, so only the admin entry ever fetches them.
const cockpitRoute = createRoute({ getParentRoute: () => rootRoute, path: COCKPIT_PREFIX }).lazy(() =>
  import('./cockpit.lazy').then((module) => module.FrameRoute),
);
const kindsRoute = createRoute({
  getParentRoute: () => cockpitRoute,
  path: '/',
  validateSearch: parseKindsSearch,
}).lazy(() =>
  import('./cockpit.lazy').then((module) => module.KindsRoute),
);
const walkRoute = createRoute({
  getParentRoute: () => cockpitRoute,
  path: 'walk',
  validateSearch: parseWalkSearch,
}).lazy(() => import('./cockpit.lazy').then((module) => module.WalkRoute));
const ruleRoute = createRoute({
  getParentRoute: () => cockpitRoute,
  path: 'rule',
  validateSearch: parseRuleSearch,
}).lazy(() => import('./cockpit.lazy').then((module) => module.RuleRoute));
const questionsRoute = createRoute({ getParentRoute: () => cockpitRoute, path: 'questions' }).lazy(() =>
  import('./cockpit.lazy').then((module) => module.QuestionsRoute),
);
const heldRoute = createRoute({
  getParentRoute: () => cockpitRoute,
  path: 'held',
  validateSearch: parseHeldSearch,
}).lazy(() => import('./cockpit.lazy').then((module) => module.HeldRoute));
const articleRoute = createRoute({
  getParentRoute: () => cockpitRoute,
  path: 'article',
  validateSearch: parseArticleSearch,
}).lazy(() => import('./cockpit.lazy').then((module) => module.ArticleRoute));

const routeTree = rootRoute.addChildren([
  indexRoute,
  projectsRoute,
  projectRoute,
  takeoffRoute,
  reviewRoute,
  onboardRoute,
  addinRoute,
  cockpitRoute.addChildren([kindsRoute, walkRoute, ruleRoute, questionsRoute, heldRoute, articleRoute]),
]);

/** The router for one entry, built from the one tree; `history` is for tests. */
export function buildRouter(entry: Entry, history?: RouterHistory) {
  return createRouter({ routeTree, context: { entry }, history });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
